import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import SetupChecklist from '../components/SetupChecklist';
import UsageWidget from '../components/UsageWidget';
import {
  Briefcase,
  FileText,
  CheckSquare,
  Calendar,
  DollarSign,
  MessageSquare,
  Upload,
  Sparkles,
  UserPlus,
  ChevronRight,
  ArrowRight,
  Clock,
  AlertCircle,
  Video,
  GripVertical,
  Plus,
  RotateCcw,
  RefreshCw,
  Lock,
  Unlock,
  X,
  Eye,
  EyeOff,
  Info,
} from 'lucide-react';
import api from '../lib/api';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ResponsiveGridLayout = WidthProvider(Responsive);

// Icon mapping
const ICON_MAP = {
  'briefcase': Briefcase,
  'file-text': FileText,
  'check-square': CheckSquare,
  'calendar': Calendar,
  'dollar-sign': DollarSign,
  'message-square': MessageSquare,
  'upload': Upload,
  'sparkles': Sparkles,
  'user-plus': UserPlus,
};

// Widget registry — defines all available widgets with their default grid positions
const WIDGET_REGISTRY = {
  'kpi-row': { label: 'KPI Stats', defaultLayout: { x: 0, y: 0, w: 12, h: 2, minW: 6, minH: 2 } },
  'deal-pipeline': { label: 'Deal Pipeline', defaultLayout: { x: 0, y: 2, w: 8, h: 5, minW: 4, minH: 3 } },
  'quick-actions': { label: 'Quick Actions', defaultLayout: { x: 8, y: 2, w: 4, h: 5, minW: 3, minH: 3 } },
  'recent-activity': { label: 'Recent Activity', defaultLayout: { x: 0, y: 7, w: 8, h: 6, minW: 4, minH: 3 } },
  'upcoming-meetings': { label: 'Upcoming Meetings', defaultLayout: { x: 8, y: 7, w: 4, h: 6, minW: 3, minH: 3 } },
  'documents-attention': { label: 'Documents Needing Attention', defaultLayout: { x: 0, y: 13, w: 8, h: 5, minW: 4, minH: 3 } },
  'tasks-due': { label: 'Tasks', defaultLayout: { x: 8, y: 13, w: 4, h: 5, minW: 3, minH: 3 } },
  'getting-started': { label: 'Getting Started', defaultLayout: { x: 0, y: 18, w: 12, h: 4, minW: 6, minH: 3 } },
};

function getDefaultLayouts() {
  const lg = Object.entries(WIDGET_REGISTRY).map(([id, w]) => ({
    i: id,
    ...w.defaultLayout,
  }));
  // For medium screens, stack to single column
  const md = lg.map(item => ({
    ...item,
    x: 0,
    w: 12,
  }));
  return { lg, md };
}


// ============================================
// WIDGET COMPONENTS
// ============================================

// Map backend English labels to i18n keys
const LABEL_MAP = {
  'Active Deals': 'dashboard.stats.activeDeals',
  'Documents': 'dashboard.stats.documents',
  'Tasks Due': 'dashboard.stats.tasksDue',
  'Meetings Coming': 'dashboard.stats.meetingsComing',
  'Invoices Due': 'dashboard.stats.invoicesDue',
  'Unread Messages': 'dashboard.stats.unreadMessages',
  'New Deal': 'dashboard.actions.newDeal',
  'Upload Document': 'dashboard.actions.uploadDocument',
  'Schedule Meeting': 'dashboard.actions.scheduleMeeting',
  'Create Invoice': 'dashboard.actions.createInvoice',
  'Create Contract': 'dashboard.actions.createContract',
  'Add Client': 'dashboard.actions.addClient',
  'Create your first deal': 'dashboard.steps.createDeal',
  'Add a client': 'dashboard.steps.addClient',
  'Upload a document': 'dashboard.steps.uploadDocument',
  'Schedule a meeting': 'dashboard.steps.scheduleMeeting',
  'Create an invoice': 'dashboard.steps.createInvoice',
  'Add your logo': 'dashboard.steps.addLogo',
  'Overdue': 'dashboard.time.overdue',
  'Today': 'dashboard.time.today',
  'Tomorrow': 'dashboard.time.tomorrow',
  'No date': 'dashboard.time.noDate',
  'Sent, awaiting response': 'dashboard.docReason.sent',
  'Not yet analyzed': 'dashboard.docReason.notAnalyzed',
  'Draft, not finalized': 'dashboard.docReason.draft',
  'Needs review': 'dashboard.docReason.needsReview',
};

function StatCard({ label, value, icon: iconName, route, statKey }) {
  const { t } = useTranslation();
  const Icon = ICON_MAP[iconName] || Briefcase;
  const KEY_TO_I18N = {
    activeDeals: 'dashboard.stats.activeDeals',
    documents: 'dashboard.stats.documents',
    tasksDue: 'dashboard.stats.tasksDue',
    meetingsComing: 'dashboard.stats.meetingsComing',
    invoicesDue: 'dashboard.stats.invoicesDue',
    unreadMessages: 'dashboard.stats.unreadMessages',
  };
  const i18nKey = statKey ? KEY_TO_I18N[statKey] : null;
  const translatedLabel = i18nKey ? t(i18nKey) : (LABEL_MAP[label] ? t(LABEL_MAP[label]) : label);
  const isZero = value === 0 || value === '$0';
  return (
    <Link to={route || '#'}>
      <Card className="hover:shadow-md transition-all hover:border-primary/30 cursor-pointer h-full">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">{translatedLabel}</p>
              <p className={`text-2xl font-bold ${isZero ? 'text-muted-foreground/50' : 'text-foreground'}`}>{value}</p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon className="h-4 w-4 text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function KpiRow({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 h-full">
      {stats.map((stat, i) => (
        <StatCard key={i} {...stat} statKey={stat.key} />
      ))}
    </div>
  );
}

function DealPipeline({ stages }) {
  const { t } = useTranslation();
  if (!stages) return null;
  const entries = Object.entries(stages);
  const total = entries.reduce((sum, [, count]) => sum + count, 0);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{t('dashboard.widgets.dealPipeline')}</CardTitle>
          <Link to="/deals">
            <Button variant="ghost" size="sm" className="h-7 text-xs">{t('dashboard.actions.viewAll')} <ArrowRight className="h-3 w-3 ml-1" /></Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">{t('dashboard.empty.deals')}</p>
        ) : (
          <>
            <div className="space-y-3">
              {entries.map(([stage, count]) => (
                <div key={stage} className="flex items-center gap-3">
                  <div className="w-28 text-xs text-muted-foreground truncate">{
                    ({'active': t('status.active'), 'pending_documents': t('status.pendingDocuments'), 'pending_signature': t('status.pendingSignature'), 'pending_payment': t('status.pendingPayment'), 'completed': t('status.completed')}[stage]) || stage.replace(/_/g, ' ')
                  }</div>
                  <div className="flex-1"><Progress value={total > 0 ? (count / total) * 100 : 0} className="h-2" /></div>
                  <div className="w-8 text-right text-sm font-medium">{count}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t flex justify-between text-xs text-muted-foreground">
              <span>Total: {total} deals</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RecentActivity({ activities }) {
  const { t } = useTranslation();
  const typeIcons = { document: FileText, deal: Briefcase, meeting: Calendar, task: CheckSquare };
  return (
    <Card className="h-full overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{t('dashboard.widgets.recentActivity')}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-y-auto" style={{ maxHeight: 'calc(100% - 60px)' }}>
        {!activities || activities.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">{t('dashboard.empty.activity')}</p>
        ) : (
          <div className="space-y-3">
            {activities.map((activity, i) => {
              const Icon = typeIcons[activity.type] || FileText;
              return (
                <Link key={i} to={activity.route || '#'} className="block">
                  <div className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{
                        activity.message ? activity.message : 
                        activity.type === 'document' ? `${t('activity.action.' + (activity.action || 'updated'))} - ${activity.filename || ''}` :
                        activity.type === 'deal' ? `${t('activity.deal')} "${activity.title || ''}" - ${t('status.' + (activity.status || 'updated'))}` :
                        ''
                      }</p>
                      <p className="text-xs text-muted-foreground">{
                        activity.time_display ? activity.time_display :
                        activity.minutes_ago !== undefined ? (
                          activity.minutes_ago < 60 ? t('time.minutesAgo', { count: activity.minutes_ago }) :
                          activity.minutes_ago < 1440 ? t('time.hoursAgo', { count: Math.floor(activity.minutes_ago / 60) }) :
                          t('time.daysAgo', { count: Math.floor(activity.minutes_ago / 1440) })
                        ) : ''
                      }</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QuickActions({ actions }) {
  const { t } = useTranslation();
  if (!actions) return null;
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{t('dashboard.widgets.quickActions')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          {actions.map((action, i) => {
            const Icon = ICON_MAP[action.icon] || Briefcase;
            return (
              <Link key={i} to={action.route}>
                <Button variant="outline" className="w-full h-auto py-2 flex flex-col items-center gap-1 text-xs font-semibold">
                  <Icon className="h-3.5 w-3.5" />
                  {LABEL_MAP[action.label] ? t(LABEL_MAP[action.label]) : action.label}
                </Button>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function UpcomingMeetings({ meetings }) {
  const { t } = useTranslation();
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{t('dashboard.widgets.upcomingMeetings')}</CardTitle>
          <Link to="/meetings"><Button variant="ghost" size="sm" className="h-7 text-xs">{t('dashboard.actions.all')} <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
        </div>
      </CardHeader>
      <CardContent>
        {!meetings || meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">{t('dashboard.empty.meetings')}</p>
        ) : (
          <div className="space-y-2">
            {meetings.map((mtg) => (
              <Link key={mtg.id} to={mtg.route} className="block">
                <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Video className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{mtg.title}</p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />{mtg.time}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TasksDue({ tasks }) {
  const { t } = useTranslation();
  const priorityColors = {
    high: 'text-red-600 dark:text-red-400',
    urgent: 'text-red-600 dark:text-red-400',
    normal: 'text-foreground',
    low: 'text-muted-foreground',
  };
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{t('dashboard.widgets.tasks')}</CardTitle>
          <Link to="/tasks"><Button variant="ghost" size="sm" className="h-7 text-xs">{t('dashboard.actions.all')} <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
        </div>
      </CardHeader>
      <CardContent>
        {!tasks || tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">{t('dashboard.empty.tasks')}</p>
        ) : (
          <div className="space-y-2">
            {tasks.map((task) => (
              <Link key={task.id} to="/tasks" className="block">
                <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <CheckSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${priorityColors[task.priority] || ''}`}>{task.title}</p>
                  </div>
                  <Badge variant={task.due === 'Overdue' ? 'destructive' : task.due === 'Today' ? 'default' : 'secondary'} className="text-[10px] shrink-0">{LABEL_MAP[task.due] ? t(LABEL_MAP[task.due]) : task.due}</Badge>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DocumentsAttention({ docs }) {
  const { t } = useTranslation();
  const statusColors = {
    sent: 'bg-blue-500/10 text-blue-600 border-blue-200',
    uploaded: 'bg-amber-500/10 text-amber-600 border-amber-200',
    draft: 'bg-slate-500/10 text-slate-600 border-slate-200',
  };
  if (!docs || docs.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />{t('dashboard.widgets.documentsAttention')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">{t('dashboard.empty.documents')}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />{t('dashboard.widgets.documentsAttention')}
          </CardTitle>
          <Link to="/documents"><Button variant="ghost" size="sm" className="h-7 text-xs">{t('dashboard.actions.all')} <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {docs.map((doc) => (
            <Link key={doc.id} to={`/documents/${doc.id}`} className="block">
              <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{doc.filename}</p>
                  <p className="text-xs text-muted-foreground">{LABEL_MAP[doc.reason] ? t(LABEL_MAP[doc.reason]) : doc.reason}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] shrink-0 ${statusColors[doc.status] || ''}`}>{doc.status}</Badge>
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function GettingStarted({ data }) {
  const { t } = useTranslation();
  if (!data) return null;
  return (
    <Card className="h-full border-primary/20">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{t('dashboard.widgets.gettingStarted')}</CardTitle>
          <span className="text-xs text-muted-foreground">{data.profile_complete}% {t('dashboard.steps.complete') || 'complete'}</span>
        </div>
      </CardHeader>
      <CardContent>
        <Progress value={data.profile_complete} className="mb-4 h-2" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {data.steps?.map((step, i) => (
            <Link key={i} to={step.done ? '#' : (step.route || '#')}
              className={`flex items-center gap-2 p-2.5 rounded-lg transition-colors text-sm ${
                step.done ? 'bg-green-500/5 text-green-700 dark:text-green-400' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}>
              <CheckSquare className={`h-4 w-4 shrink-0 ${step.done ? 'text-green-500' : 'text-muted-foreground/50'}`} />
              <span className={step.done ? 'line-through opacity-60' : ''}>{LABEL_MAP[step.label] ? t(LABEL_MAP[step.label]) : step.label}</span>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}



// ============================================
// AI BRIEFING
// ============================================
function AiBriefing() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const { data: briefingData, isLoading, refetch } = useQuery({
    queryKey: ['dashboard-briefing'],
    queryFn: () => api.get('/dashboard/briefing'),
    staleTime: 3600000, // 1 hour
    refetchOnWindowFocus: false,
  });

  if (dismissed || (!isLoading && !briefingData?.briefing)) return null;

  return (
    <Card className="h-full border-l-4 border-l-primary">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('dashboard.briefing.title')}</p>
            </div>
            {isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <p className="text-sm text-foreground leading-relaxed">{briefingData?.briefing === "Nothing urgent right now. Your workspace is in good shape." ? t("dashboard.briefing.default") : briefingData?.briefing}</p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => refetch()} title="Refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDismissed(true)} title="Dismiss">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


// ============================================
// AI BRIEFING
// ============================================

// ============================================
// WIDGET PICKER DIALOG
// ============================================
function WidgetPicker({ hiddenWidgets, onAdd, onClose }) {
  if (!hiddenWidgets || hiddenWidgets.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Add Widget</h3>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="space-y-2">
          {hiddenWidgets.map((id) => (
            <Button key={id} variant="outline" className="w-full justify-between" onClick={() => { onAdd(id); onClose(); }}>
              {WIDGET_REGISTRY[id]?.label || id}
              <Plus className="h-4 w-4" />
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}


// ============================================
// LOADING SKELETON
// ============================================
function DashboardSkeleton() {
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-40" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-[88px] rounded-xl" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
        <div className="space-y-6">
      <SetupChecklist />
      <UsageWidget />

          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </div>
    </div>
  );
}


// ============================================
// MAIN DASHBOARD
// ============================================
export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [hiddenWidgets, setHiddenWidgets] = useState([]);
  const [layouts, setLayouts] = useState(null);
  const [layoutInitialized, setLayoutInitialized] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-unified'],
    queryFn: () => api.get('/dashboard/unified'),
    staleTime: 30000,
    refetchInterval: editing ? false : 60000,
  });

  // Initialize layouts from saved data or defaults
  useEffect(() => {
    if (!data || layoutInitialized) return;

    if (data.saved_layout) {
      // Restore saved layout
      try {
        const saved = typeof data.saved_layout === 'string' ? JSON.parse(data.saved_layout) : data.saved_layout;
        setLayouts(saved);
      } catch {
        setLayouts(getDefaultLayouts());
      }
    } else {
      setLayouts(getDefaultLayouts());
    }

    setHiddenWidgets(data.hidden_widgets || []);
    setLayoutInitialized(true);
  }, [data, layoutInitialized]);

  // Save layout mutation
  const saveMutation = useMutation({
    mutationFn: (payload) => api.put('/dashboard/layout', payload),
    onSuccess: () => toast.success('Dashboard layout saved'),
    onError: () => toast.error('Failed to save layout'),
  });

  const resetMutation = useMutation({
    mutationFn: () => api.delete('/dashboard/layout'),
    onSuccess: () => {
      setLayouts(getDefaultLayouts());
      setHiddenWidgets([]);
      toast.success('Dashboard reset to default');
    },
    onError: () => toast.error('Failed to reset layout'),
  });

  const handleLayoutChange = useCallback((currentLayout, allLayouts) => {
    if (!editing) return;
    setLayouts(allLayouts);
  }, [editing]);

  const handleSave = useCallback(() => {
    saveMutation.mutate({ layout: layouts, hidden_widgets: hiddenWidgets });
    setEditing(false);
  }, [layouts, hiddenWidgets, saveMutation]);

  const handleReset = useCallback(() => {
    resetMutation.mutate();
    setEditing(false);
  }, [resetMutation]);

  const handleHideWidget = useCallback((widgetId) => {
    setHiddenWidgets(prev => [...prev, widgetId]);
    // Remove from all layout breakpoints
    setLayouts(prev => {
      if (!prev) return prev;
      const updated = {};
      for (const [bp, items] of Object.entries(prev)) {
        updated[bp] = items.filter(item => item.i !== widgetId);
      }
      return updated;
    });
  }, []);

  const handleAddWidget = useCallback((widgetId) => {
    setHiddenWidgets(prev => prev.filter(id => id !== widgetId));
    // Add back to layout with default position
    const def = WIDGET_REGISTRY[widgetId]?.defaultLayout;
    if (def) {
      setLayouts(prev => {
        if (!prev) return prev;
        const updated = {};
        for (const [bp, items] of Object.entries(prev)) {
          updated[bp] = [...items, { i: widgetId, ...def }];
        }
        return updated;
      });
    }
  }, []);

  // Map widget IDs to their render functions
  const widgetRenderers = useMemo(() => ({
    'kpi-row': () => <KpiRow stats={data?.stats} />,
    'deal-pipeline': () => <DealPipeline stages={data?.deal_pipeline?.stages} />,
    'quick-actions': () => <QuickActions actions={data?.quick_actions} />,
    'recent-activity': () => <RecentActivity activities={data?.recent_activity} />,
    'upcoming-meetings': () => <UpcomingMeetings meetings={data?.upcoming_meetings} />,
    'documents-attention': () => <DocumentsAttention docs={data?.documents_attention} />,
    'tasks-due': () => <TasksDue tasks={data?.tasks_due} />,
    'getting-started': () => <GettingStarted data={data?.getting_started} />,
  }), [data]);

  if (isLoading || !data || !layouts) {
    return <DashboardSkeleton />;
  }

  // Filter out hidden widgets and getting-started if complete
  const visibleWidgetIds = Object.keys(WIDGET_REGISTRY).filter(id => {
    if (hiddenWidgets.includes(id)) return false;
    if (id === 'getting-started' && !data.getting_started) return false;
    return true;
  });

  return (
    <div className="p-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight">{(() => {
            const hour = new Date().getHours();
            const greeting = hour < 12 ? t("dashboard.greeting.morning") : hour < 18 ? t("dashboard.greeting.afternoon") : t("dashboard.greeting.evening");
            const name = user?.name?.split(" ")[0] || "";
            return name ? greeting + ", " + name : greeting;
          })()}</h1>
          <p className="text-sm text-muted-foreground">{t('dashboard.greeting.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowPicker(true)} disabled={hiddenWidgets.length === 0}>
                <Plus className="h-4 w-4" /> Add Widget
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleReset}>
                <RotateCcw className="h-4 w-4" /> Reset
              </Button>
              <Button size="sm" className="gap-1.5" onClick={handleSave}>
                <Lock className="h-4 w-4" /> Save Layout
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditing(true)}>
                <Unlock className="h-4 w-4" /> {t('dashboard.actions.customize')}
              </Button>
              <Link to="/quick-contract">
                <Button size="sm" className="gap-1.5">
                  <Sparkles className="h-4 w-4" /> {t('dashboard.actions.createContract')}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Editing hint */}
      {editing && (
        <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm text-muted-foreground">
          Drag widgets to rearrange. Resize from the bottom-right corner. Click the X on a widget to hide it.
        </div>
      )}

      {/* AI Briefing - static, above grid */}
      <AiBriefing />

      {/* Grid Layout */}
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1024, md: 768 }}
        cols={{ lg: 12, md: 12 }}
        rowHeight={40}
        isDraggable={editing}
        isResizable={editing}
        onLayoutChange={handleLayoutChange}
        draggableHandle=".drag-handle"
        compactType="vertical"
        margin={[16, 16]}
      >
        {visibleWidgetIds.map((widgetId) => {
          const renderer = widgetRenderers[widgetId];
          if (!renderer) return null;

          return (
            <div key={widgetId} className="relative">
              {editing && (
                <div className="absolute top-1 right-1 z-10 flex items-center gap-1">
                  <div className="drag-handle cursor-grab active:cursor-grabbing p-1 rounded bg-muted/80 hover:bg-muted">
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <button
                    className="p-1 rounded bg-muted/80 hover:bg-destructive/20 hover:text-destructive transition-colors"
                    onClick={() => handleHideWidget(widgetId)}
                    title="Hide widget"
                  >
                    <EyeOff className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {renderer()}
            </div>
          );
        })}
      </ResponsiveGridLayout>

      {/* Widget Picker */}
      {showPicker && (
        <WidgetPicker
          hiddenWidgets={hiddenWidgets}
          onAdd={handleAddWidget}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
